import {forwardRef, memo, type ReactNode, useEffect} from 'react';
import {cn} from '../lib/cn';
import type {SelectOption} from '../lib/selector-option';
import {Radio, type RadioOption} from '../radio';
import {RadioGroupItem} from '../radio-group';

type RadioCardsSelectOption = SelectOption<unknown, string, string>;

export type RadioCardOption = Pick<RadioCardsSelectOption, 'label' | 'value'> & {
  description?: ReactNode | string;
  disabled?: boolean;
};

const isValueSelectOption = (
  value?: RadioCardsSelectOption | string | null,
): value is RadioCardsSelectOption => {
  return (value as RadioCardsSelectOption)?.value !== undefined;
};

type OptionsCardsProps = {
  options: RadioCardOption[];
  onChange: (value: RadioCardsSelectOption) => void;
  name: string;
  value?: string | RadioCardsSelectOption | null;
  defaultValue?: string;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  error?: string;
  className?: string;
  cardClassName?: string;
};

const RadioCardsGroup = forwardRef<HTMLDivElement, OptionsCardsProps>(
  (
    {
      options,
      onChange,
      value,
      defaultValue,
      disabled,
      readOnly,
      error,
      loading,
      className,
      cardClassName,
    },
    ref,
  ) => {
    const handleChange = (option: RadioOption) => {
      if (!disabled && !readOnly) {
        onChange({label: option.label, value: option.value});
      }
    };

    useEffect(
      function setDefaultValue() {
        const option = options.find(opt => opt.value === defaultValue);

        if (defaultValue && option && !value) {
          onChange(option);
        }
      },
      [defaultValue, onChange, options, value],
    );

    const currentValue = isValueSelectOption(value) ? value.value : value;

    return (
      <>
        <Radio
          ref={ref}
          options={options as RadioOption[]}
          onChange={handleChange}
          value={currentValue || defaultValue}
          disabled={disabled}
          className={cn('flex flex-wrap gap-4', className)}
          renderOption={({option, isSelected}) => {
            return (
              <div
                className={cn(
                  'radio-card',
                  'relative box-border flex min-h-[168px] w-[352px] items-start gap-4 rounded-lg border border-[var(--chekin-border-default)] bg-white p-4 pl-14',
                  !disabled &&
                    !loading &&
                    !readOnly &&
                    'cursor-pointer hover:border-[var(--brand-color,var(--chekin-color-brand-blue))]',
                  isSelected &&
                    'border-2 border-[var(--brand-color,var(--chekin-color-brand-blue))] bg-[var(--chekin-color-surface-autocomplete)]',
                  disabled && 'cursor-not-allowed opacity-50',
                  loading && 'cursor-progress opacity-50',
                  readOnly && 'cursor-default',
                  error && 'border-[var(--chekin-red-500)]',
                  cardClassName,
                )}
              >
                <div>
                  <RadioGroupItem
                    value={option.value}
                    id={`radio-${option.value}`}
                    disabled={disabled || option.disabled}
                  />
                </div>
                <div className="w-full cursor-pointer">
                  <label
                    htmlFor={`radio-${option.value}`}
                    className={cn(
                      'radio_input flex select-none text-left align-middle text-xl font-bold leading-6 text-[var(--chekin-color-brand-navy)]',
                      readOnly ? 'cursor-default' : 'cursor-pointer',
                    )}
                  >
                    {option.label}
                  </label>

                  {(option as RadioCardOption).description && (
                    <div className="radioCardDescription mt-2">
                      {(option as RadioCardOption).description}
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        />
        {error && (
          <div className="mt-2 text-left text-sm text-[var(--chekin-red-500)]">
            {error}
          </div>
        )}
      </>
    );
  },
);

const MemoizedRadioCardsGroup = memo(RadioCardsGroup);
export {MemoizedRadioCardsGroup as RadioCardsGroup};
export type {OptionsCardsProps};
